import { Component, Inject, NgZone, OnDestroy, Optional, ViewChild } from '@angular/core';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { TranslateModule } from '@ngx-translate/core';

import { OperationAdminDto } from './model/operation-admin-dto';
import { StompService } from '../../services/stomp/stomp.service';
import { AuthCoreService } from '../../services/auth-core.service';
import { AbstractSigaleComponent } from '../abstract-sigale.component';
import { Subscription } from 'rxjs';
import { NotifierService } from '../../services/notifier.service';
import { Observer } from '../../models/observer.interface';
import { LoginModalComponent } from '../angular/login-modal/login-modal.component';
import { isNullOrUndefined } from '../../utils/functions';

/**
 * Composant permettant de se connecter au topic des opérations d'admin et de réaliser les actions selon l'opération.
 */
@Component({
	selector: 'sigale-operations-admin-component',
	templateUrl: './operations-admin.component.html',
	styleUrls: ['./operations-admin.component.scss'],
	standalone: true,
	imports: [
		ToastModule,
		TranslateModule,
		LoginModalComponent
	]
})
export class OperationsAdminComponent extends AbstractSigaleComponent implements Observer, OnDestroy {

	static readonly TOPIC_OPERATION_ADMIN: string = 'operation-admin';
	readonly KEY_TOAST = 'operation-admin-toast';

	@ViewChild('sigaleLoginModal') sigaleLoginModal?: LoginModalComponent;

	operationAdminAbonnement: Subscription;

	constructor(
		@Optional() private notifierService: NotifierService,
		@Optional() private stompService: StompService,
		private ngZone: NgZone,
		@Optional() @Inject('$scope') private $scope: any,
		private authCoreService: AuthCoreService,
		private messageService: MessageService
	) {
		super();
	}

	ngOnInit(): void {
		super.ngOnInit();
		if (this.stompService) {
			// le stompService fonctionne sur toutes les applications, en cohabitation ou non
			this.operationAdminAbonnement = this.stompService.abonnerTopic(OperationsAdminComponent.TOPIC_OPERATION_ADMIN, (operationAdminDto: OperationAdminDto) => {
				this.ngZone.run(() => this.onNotificationOperationRecu(operationAdminDto));
			});
		}
		else if (!this.modeApplicationCohabitation) {
			this.notifierService.subscribeToTopic(this, OperationsAdminComponent.TOPIC_OPERATION_ADMIN);
		}
		else if (this.$scope) {
			this.$scope.$on('app:dataReceived:OPERATION_ADMIN', (event, operationAdminDto: OperationAdminDto) => this.onNotificationOperationRecu(operationAdminDto));
		}

		setTimeout(() => {
			// le chargement des langues se fait après un petit délai car dans le cas contraire, on a une erreur js dans la console front "ExpressionChangedAfterItHasBeenCheckedError"
			this.translateService.setTranslation('FR', this.traductionFR(), true);
			this.translateService.setTranslation('IT', this.traductionIT(), true);
		}, 1000);
	}

	ngOnDestroy(): void {
		if (this.stompService) {
			this.stompService.desabonner(this.operationAdminAbonnement);
		}
		else if (!this.modeApplicationCohabitation) {
			this.notifierService.disconnectFromTopic(OperationsAdminComponent.TOPIC_OPERATION_ADMIN);
		}
	}

	/**
	 * Callback appelé lors de la réception d'une notification.
	 *
	 * @param response la réponse de la notification.
	 */
	public notify(response: any): void {
		if (!this.modeApplicationCohabitation) {
			this.ngZone.run(() => {
				const notificationOperationAdminDto = JSON.parse(response).data;
				this.onNotificationOperationRecu(notificationOperationAdminDto);
			});
		}
	}

	/**
	 * Cette méthode permet d'implémenter les actions selon le code de l'opération.
	 *
	 * @param notificationOperationAdminDto La notification reçue.
	 */
	onNotificationOperationRecu(notificationOperationAdminDto: OperationAdminDto) {
		const categorieUtilisateurConcernee: boolean = notificationOperationAdminDto?.inclureAdmin || !this.authCoreService.isAdminConnecte();
		if (!notificationOperationAdminDto) {
			return;
		}
		switch (notificationOperationAdminDto.codeTypeOperationAdmin) {
			case 'RAFRAICHIR':
				if (categorieUtilisateurConcernee) {
					this.reloadOngletChrome();
				}
				break;
			case 'DECONNECTER':
				const operationAdminDto: OperationAdminDto = new OperationAdminDto().deserialize(notificationOperationAdminDto);
				if (!this.sigaleLoginModal.visible && (isNullOrUndefined(this.authCoreService.loginUtilisateurConnecte()) || categorieUtilisateurConcernee && operationAdminDto.doitDeconnecter(this.authCoreService.loginUtilisateurConnecte()))) {
					this.messageService.add({
						severity: 'info',
						detail: this.translateService.instant('SIGALE_ANGULAR.COMPONENTS.OPERATION_ADMIN_CONNEXION.MESSAGE_RAFRAICHISSEMENT_NECESSAIRE'),
						sticky: true,
						key: this.KEY_TOAST
					});
					this.sigaleLoginModal.ouvrir();
				}
				break;
			// AJOUT-OPERATION: Ajouter le type ici et implémenter l'action.
			default:
				break;
		}
	}

	/**
	 * Permet de recharger l'écran.
	 */
	reloadOngletChrome(): void {
		window.location.reload();
	}

	traductionFR(): any {
		return {
			'SIGALE_ANGULAR': {
				'COMPONENTS': {
					'OPERATION_ADMIN_CONNEXION': {
						'MESSAGE_RAFRAICHISSEMENT_NECESSAIRE': 'Vos droits ont potentiellement été changés. Veuillez terminer votre opération en cours et ensuite recharger votre écran en appuyant sur la touche F5.'
					}
				}
			}
		};
	}

	traductionIT(): any {
		return {
			'SIGALE_ANGULAR': {
				'COMPONENTS': {
					'OPERATION_ADMIN_CONNEXION': {
						'MESSAGE_RAFRAICHISSEMENT_NECESSAIRE': 'Probabilmente i vostri accessi sono stati modificati. Si prega di terminare l\'operazione in corso e di ricaricare la pagina cliccando sul tasto F5.'
					}
				}
			}
		};
	}
}
